import axios from 'axios'
import { Message } from 'element-ui'
import apiMonitor from './apiMonitor'

const http = axios.create({
	baseURL: process.env.VUE_APP_BASE_API,
	timeout: 1000 * 90,
	// withCredentials: true
})

// 请求重试配置
const MAX_RETRY_COUNT = 3 // 最大重试次数
const RETRY_DELAY = 1000 // 重试延迟(毫秒)

// Token 刷新互斥锁
let isRefreshing = false;
let refreshSubscribers = [];
// 刷新失败冷却：网络故障时避免 401 风暴反复触发刷新接口
let lastRefreshFailTime = 0;
const REFRESH_FAIL_COOLDOWN = 30000; // 30s

function onTokenRefreshed(token) {
	refreshSubscribers.forEach(cb => cb(token));
	refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
	refreshSubscribers.push(callback);
}

// 刷新失败：拒绝所有排队请求，避免 Promise 悬挂
function onTokenRefreshFailed() {
	refreshSubscribers.forEach(cb => cb(null, new Error('Token refresh failed')));
	refreshSubscribers = [];
}

// 业务性失效（refresh token 无效/账号封禁）：清理会话并跳登录
// （必须清理 storage，否则路由守卫会把 /login 弹回 /home 形成整页刷新循环）
function forceLogout() {
	sessionStorage.removeItem("accessToken");
	sessionStorage.removeItem("refreshToken");
	location.href = "/";
}

// 判断是否为 Token 过期响应（业务码 1002 未认证/Token失效 或 2003 Token过期）
function isTokenExpiredResponse(status, code) {
	return status === 401 && (code === 1002 || code === 2003);
}

// Token 刷新统一流程：互斥锁 + 请求队列 + 重放（成功分支与 401 错误分支共用）
function handleTokenRefresh(originalConfig) {
	// 刷新失败冷却期内不再尝试（静默失败，30s 后自愈重试）
	if (Date.now() - lastRefreshFailTime < REFRESH_FAIL_COOLDOWN) {
		return Promise.reject(new Error('Token refresh cooling down'));
	}

	// 刷新请求本身返回业务性失效（401），说明 refreshToken 已失效或账号被封禁：登出
	if (originalConfig.__skipAuthRetry) {
		isRefreshing = false;
		onTokenRefreshFailed();
		forceLogout();
		return Promise.reject(new Error('Token expired'));
	}

	if (!isRefreshing) {
		isRefreshing = true;
		let refreshToken = sessionStorage.getItem("refreshToken");
		if (!refreshToken) {
			isRefreshing = false;
			onTokenRefreshFailed();
			forceLogout();
			return Promise.reject(new Error('Token expired'));
		}

		return http({
			method: 'post',
			url: '/api/v1/customer/refresh_token',
			data: { ref_token: refreshToken },
			__skipAuthRetry: true
		}).then(data => {
			// 保存新token
			sessionStorage.setItem("accessToken", data.token);
			sessionStorage.setItem("refreshToken", data.ref_token);
			isRefreshing = false;
			// 通知所有等待中的请求使用新token重试
			onTokenRefreshed(data.token);
			// 标记重放请求：重放仍 401 时直接登出，防止刷新-重放循环
			originalConfig.__tokenRetried = true;
			// 重新发送当前请求
			return http(originalConfig);
		}).catch((err) => {
			isRefreshing = false;
			onTokenRefreshFailed();
			lastRefreshFailTime = Date.now();
			// 仅业务性失效（refresh token 无效/封禁，HTTP 401）强制登出；
			// 网络/5xx/429 等瞬时故障静默失败，冷却生效后 30s 自愈重试，避免整页刷新循环
			const status = err && err.response && err.response.status;
			if (status === 401) {
				forceLogout();
			} else {
				console.warn('[HTTP] Token 刷新失败（瞬时故障，冷却后自动重试）', err);
			}
			return Promise.reject(new Error('Token refresh failed'));
		});
	} else {
		// 已有刷新请求在进行中，当前请求排队等待（刷新失败时由 onTokenRefreshFailed 拒绝）
		return new Promise((resolve, reject) => {
			addRefreshSubscriber((token, err) => {
				if (err) return reject(err);
				originalConfig.__tokenRetried = true;
				resolve(http(originalConfig));
			});
		});
	}
}

/**
 * 请求拦截
 */
http.interceptors.request.use(config => {
	let accessToken = sessionStorage.getItem("accessToken");
	if (accessToken) {
		config.headers.token = encodeURIComponent(accessToken);
	}
	// 添加请求时间戳，用于性能监控
	config.metadata = { startTime: new Date() };
	// 启动API监控
	apiMonitor.startRequest(config);
	return config
}, error => {
	console.error('[HTTP Request Error]', error);
	apiMonitor.failRequest(error.config || {}, error);
	return Promise.reject(error)
})

/**
 * 响应拦截
 */
http.interceptors.response.use(async response => {
	// 记录请求耗时
	if (response.config.metadata) {
		const duration = new Date() - response.config.metadata.startTime;
		if (duration > 3000) { // 超过3秒的请求标记为慢请求
			console.warn(`[HTTP Slow Request] ${response.config.method.toUpperCase()} ${response.config.url} - ${duration}ms`);
		}
	}

	if (response.data.code == 0) {
		// 记录成功请求
		apiMonitor.endRequest(response.config, response);
		return response.data.result;
	} else if (response.data.code == 1002 || response.data.code == 2003) {
		// Token过期，尝试刷新（使用互斥锁防止并发刷新）
		// 刷新后重放仍失效：不再循环刷新，直接登出
		if (response.config.__tokenRetried) {
			forceLogout();
			return Promise.reject(new Error('Token expired'));
		}
		return handleTokenRefresh(response.config);
	} else {
		// 业务错误
		const errorMsg = response.data.msg || '请求失败';
		apiMonitor.failRequest(response.config, new Error(errorMsg));
		Message({
			message: errorMsg,
			type: 'error',
			duration: 2000,
			customClass: 'element-error-message-zindex'
		})
		return Promise.reject(response.data)
	}
}, error => {
	// HTTP错误处理
	const config = error.config;
	
	// 如果是取消请求，不处理
	if (axios.isCancel(error)) {
		return Promise.reject(error);
	}
	
	// 刷新请求自身失败（500/超时等非401错误）：释放互斥锁并拒绝排队请求，避免死锁悬挂
	if (config && config.__skipAuthRetry) {
		isRefreshing = false;
		onTokenRefreshFailed();
	}
	// 添加重试逻辑（排除刷新 token 请求）
	config.__retryCount = config.__retryCount || 0;
	
	if (!config.__skipAuthRetry &&
	    config.__retryCount < MAX_RETRY_COUNT && 
	    (!error.response || error.response.status >= 500)) {
		config.__retryCount++;
		
		console.warn(`[HTTP Retry] ${config.method.toUpperCase()} ${config.url} - Attempt ${config.__retryCount}/${MAX_RETRY_COUNT}`);
		
		// 延迟重试
		return new Promise(resolve => {
			setTimeout(() => {
				resolve(http(config));
			}, RETRY_DELAY);
		});
	}
	
	// 错误消息处理
	let errorMsg = '服务器异常';
	
	if (error.response) {
		switch (error.response.status) {
			case 400:
				errorMsg = error.response.data?.msg || '请求参数错误';
				break;
			case 401:
				// Token过期（业务码1002/2003）走静默刷新，其余401直接跳登录
				// 刷新后重放仍 401：不再循环刷新，直接登出
				if (config.__tokenRetried) {
					errorMsg = '登录已过期，请重新登录';
					location.href = "/";
					break;
				}
				if (isTokenExpiredResponse(401, error.response.data?.code)) {
					return handleTokenRefresh(config);
				}
				errorMsg = '登录已过期，请重新登录';
				location.href = "/";
				break;
			case 403:
				errorMsg = '没有权限访问';
				break;
			case 404:
				errorMsg = '请求的资源不存在';
				break;
			case 405:
				errorMsg = '请求方法不允许';
				break;
			case 500:
				errorMsg = '服务器内部错误';
				break;
			case 502:
				errorMsg = '网关错误';
				break;
			case 503:
				errorMsg = '服务不可用';
				break;
			case 504:
				errorMsg = '网关超时';
				break;
			default:
				errorMsg = error.response.data?.msg || `请求失败(${error.response.status})`;
		}
	} else if (error.message) {
		if (error.message.includes('timeout')) {
			errorMsg = '请求超时，请检查网络连接';
		} else if (error.message.includes('Network')) {
			errorMsg = '网络连接失败';
		}
	}
	
	Message({
		message: errorMsg,
		type: 'error',
		duration: 2000,
		customClass: 'element-error-message-zindex'
	})
	
	console.error(`[HTTP Error] ${config?.method?.toUpperCase()} ${config?.url}`, error);
	// 记录失败请求
	apiMonitor.failRequest(config, error);
	return Promise.reject(error)
})

export default http
